import { networkId } from '@/composables/useNetwork';
import { Config } from '@/lib/config/types';
import configs from '@/lib/config';
import { Network } from '@/lib/config/types';
import template from '@/lib/utils/template';

interface Env {
  APP_ENV: string;
  APP_DOMAIN: string;
  APP_HOST: string;
  API_URL: string;
  PLATFORM_API_URL: string;
  TURNSTILE_SITE_KEY: string;
  IPFS_NODE: string;
  BLOCKNATIVE_DAPP_ID: string;
  ALCHEMY_KEY: string;
  GRAPH_KEY: string;
  INFURA_PROJECT_ID: string;
  ENABLE_STABLE_POOLS: boolean;
  WALLET_SCREENING: boolean;
  // Contract addresses
  ORCHESTRATOR: string;
  ETH_ORCHESTRATOR: string;
  TOKENFACTORY: string;
  ETH_TOKEN_FACTORY: string;
  ATOMIC_CONTRACT_ADDRESS: string;
  ETH_ATOMIC_CONTRACT_ADDRESS: string;
  BPOOL_CONTRACT_ADDRESS: string;
  ETH_BPOOL_CONTRACT_ADDRESS: string;
  RISKONBTC_TOKEN_ADDRESS: string;
  RISKOFFBTC_TOKEN_ADDRESS: string;
  RISKONETH_TOKEN_ADDRESS: string;
  RISKOFFETH_TOKEN_ADDRESS: string;
  BTC_TOKEN_ADDRESS: string;
  ETH_TOKEN_ADDRESS: string;
  UNDERLYING_BTC_TOKEN_ADDRESS: string;
  UNDERLYING_ETH_TOKEN_ADDRESS: string;
  DAPP_URL: string;
  // RiskProtocol API endpoints (per underlying)
  BTC_API_URL: string;
  ETH_API_URL: string;
  // RiskProtocol API product identifiers (per underlying)
  BTC_PRODUCT_ID: string;
  ETH_PRODUCT_ID: string;
}
export default class ConfigService {
  public get env(): Env {
    return {
      APP_ENV: import.meta.env.VITE_ENV || 'development',
      APP_DOMAIN: import.meta.env.VITE_DOMAIN || 'app.balancer.fi',
      APP_HOST: import.meta.env.VITE_HOST || 'balancer.fi',
      API_URL:
        import.meta.env.VITE_API_URL || 'https://api-v3.balancer.fi/graphql',
      PLATFORM_API_URL:
        import.meta.env.VITE_PLATFORM_API_URL ||
        'https://platform-api.riskprotocol.io',
      TURNSTILE_SITE_KEY: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',
      IPFS_NODE: import.meta.env.VITE_IPFS_NODE || 'cloudflare-ipfs.com',
      BLOCKNATIVE_DAPP_ID:
        import.meta.env.VITE_BLOCKNATIVE_DAPP_ID || 'MISSING_KEY',
      ALCHEMY_KEY:
        import.meta.env.VITE_ALCHEMY_KEY ||
        this.getNetworkConfig(networkId.value).keys.alchemy ||
        'MISSING_KEY',
      GRAPH_KEY:
        import.meta.env.VITE_ENV === 'development'
          ? import.meta.env.VITE_GRAPH_KEY_DEV || 'MISSING_KEY'
          : import.meta.env.VITE_GRAPH_KEY ||
            this.getNetworkConfig(networkId.value).keys.graph ||
            'MISSING_KEY',
      INFURA_PROJECT_ID:
        import.meta.env.VITE_INFURA_PROJECT_ID ||
        this.getNetworkConfig(networkId.value).keys.infura ||
        'MISSING_KEY',
      ENABLE_STABLE_POOLS: import.meta.env.VITE_ENABLE_STABLE_POOLS === 'true',
      WALLET_SCREENING: import.meta.env.VITE_WALLET_SCREENING === 'true',
      // Contract addresses
      ORCHESTRATOR:
        import.meta.env.VITE_ORCHESTRATOR ||
        '0xB7CFD515F2FC8d42cc301a8052d143A8B28Aa87B',
      ETH_ORCHESTRATOR:
        import.meta.env.VITE_ETH_ORCHESTRATOR ||
        '0xe711d780FA97328a11AF495b2Bfd414Ad9F2d0D1',
      TOKENFACTORY:
        import.meta.env.VITE_TOKENFACTORY ||
        '0xb7c8c55DEB9f243745B859D5ed1fa744B5df9bbd',
      ETH_TOKEN_FACTORY:
        import.meta.env.VITE_ETH_TOKEN_FACTORY ||
        '0xe711Bf108EBb4A1690165ca72DB5E2a6151CEe2D',
      ATOMIC_CONTRACT_ADDRESS:
        import.meta.env.VITE_ATOMIC_CONTRACT_ADDRESS ||
        '0xB7C89fb4C7DCdf0567f29117C530431D5EF92d45',
      ETH_ATOMIC_CONTRACT_ADDRESS:
        import.meta.env.VITE_ETH_ATOMIC_CONTRACT_ADDRESS ||
        '0xe7110Dc48e2Ac9f9d920830660A26725F310D863',
      BPOOL_CONTRACT_ADDRESS:
        import.meta.env.VITE_BPOOL_CONTRACT_ADDRESS ||
        '0x06376B94a26f5095Fb8f391a382e390f76BdEF9C',
      ETH_BPOOL_CONTRACT_ADDRESS:
        import.meta.env.VITE_ETH_BPOOL_CONTRACT_ADDRESS ||
        '0x3ce4b38D779C645AE9a780594FA1E6E54243621e',
      RISKONBTC_TOKEN_ADDRESS:
        import.meta.env.VITE_RISKONBTC_TOKEN_ADDRESS ||
        '0xB7Cc6a284525EeAd6dd325caa958745FCDE3f65C',
      RISKOFFBTC_TOKEN_ADDRESS:
        import.meta.env.VITE_RISKOFFBTC_TOKEN_ADDRESS ||
        '0xb7Cf7c55c00678192dac436Dc1270E1cbf3EDdfa',
      RISKONETH_TOKEN_ADDRESS:
        import.meta.env.VITE_RISKONETH_TOKEN_ADDRESS ||
        '0xE71174De21406E17d5Be051686b090EfE90BB602',
      RISKOFFETH_TOKEN_ADDRESS:
        import.meta.env.VITE_RISKOFFETH_TOKEN_ADDRESS ||
        '0xe711b751805BA06F3c63dDA0686C0e74c1A218A7',
      // Lowercased: these are used as lookup keys against the pricing API's
      // getBalancerPrices response, whose addresses are lowercase and matched
      // case-sensitively by the SDK.
      BTC_TOKEN_ADDRESS: (
        import.meta.env.VITE_BTC_TOKEN_ADDRESS ||
        '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599'
      ).toLowerCase(),
      ETH_TOKEN_ADDRESS: (
        import.meta.env.VITE_ETH_TOKEN_ADDRESS ||
        '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2'
      ).toLowerCase(),
      UNDERLYING_BTC_TOKEN_ADDRESS:
        import.meta.env.VITE_UNDERLYING_BTC_TOKEN_ADDRESS ||
        '0x8af61F025562cE808c3066dC990450Ea546246fA',
      UNDERLYING_ETH_TOKEN_ADDRESS:
        import.meta.env.VITE_UNDERLYING_ETH_TOKEN_ADDRESS ||
        '0x7D51fD334CFc1A0C258C4422b011EbbB9093B11b',
      DAPP_URL: import.meta.env.VITE_DAPP_URL || '',
      BTC_API_URL:
        import.meta.env.VITE_BTC_API_URL ||
        'https://btc-arb-api.riskprotocol.io',
      ETH_API_URL:
        import.meta.env.VITE_ETH_API_URL ||
        'https://eth-arb-api.riskprotocol.io',
      BTC_PRODUCT_ID: import.meta.env.VITE_BTC_PRODUCT_ID || 'btc-30',
      ETH_PRODUCT_ID: import.meta.env.VITE_ETH_PRODUCT_ID || 'eth-30',
    };
  }

  public get isDevEnv(): boolean {
    return this.env.APP_ENV === 'development';
  }

  public get network(): Config {
    return configs[networkId.value];
  }

  public getNetworkConfig(key: Network): Config {
    if (!Object.keys(configs).includes(key?.toString()))
      throw new Error(`No config for network key: ${key}`);
    return configs[key];
  }

  public getNetworkRpc(network: Network): string {
    const networkConfig = this.getNetworkConfig(network);

    return template(
      import.meta.env[`VITE_RPC_URL_${network}`] || networkConfig.rpc,
      {
        INFURA_KEY: networkConfig.keys.infura,
        ALCHEMY_KEY: networkConfig.keys.alchemy,
      }
    );
  }

  public get rpc(): string {
    return template(
      import.meta.env[`VITE_RPC_URL_${networkId.value}`] ||
        this.getNetworkConfig(networkId.value).rpc,
      {
        INFURA_KEY: this.env.INFURA_PROJECT_ID,
        ALCHEMY_KEY: this.env.ALCHEMY_KEY,
      }
    );
  }

  public get subgraphUrls(): string[] | void {
    return this.network.subgraphs.main?.map(url => {
      if (url.includes('GRAPH_KEY')) {
        return template(url, {
          GRAPH_KEY: this.env.GRAPH_KEY,
        });
      }
      return url;
    });
  }

  public get ws(): string {
    return template(this.network.ws, {
      INFURA_KEY: this.env.INFURA_PROJECT_ID,
      ALCHEMY_KEY: this.env.ALCHEMY_KEY,
    });
  }
}

export const configService = new ConfigService();
